import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import pick from 'lodash-es/pick';
import queryString from 'query-string';

import { ApiAuthResponse } from '../lib/api/Auth';

import { useApi } from '../contexts/ApiContext';
import { useUpdateConsent } from '../contexts/ConsentContext';
import { useFacebookSdk } from '../contexts/FacebookContext';
import useCurrentUrl from './useCurrentUrl';

// import { updateConsent } from '../contexts/ConsentContext';

export type AuthResponse = ApiAuthResponse;

export interface LoginRequest {
    email?: string | null;
    phone?: string | null;
    source?: string | null;
    subscription?: string | string[] | null;
}

export interface ProviderLoginRequest {
    token: string;
    source?: string | null;
    subscription?: string | string[] | null;
}

type AuthQuery = {
    _cio_id?: string;
    _cio_did?: string;
};

function getAuthQuery(): AuthQuery {
    return pick(
        queryString.parse(typeof window !== 'undefined' ? window.location.search : '') || {},
        ['_cio_id', '_cio_did'],
    ) as AuthQuery;
}

export function updateAuthUser(queryClient, user: User | null) {
    const data = queryClient.getQueryData(['auth']);
    queryClient.setQueryData(['auth'], { ...data, user });
}

export function updateAuthAudiences(queryClient, audiences) {
    const data = queryClient.getQueryData(['auth']);
    queryClient.setQueryData(['auth'], { ...data, audiences });
}

export function prefetchAuthUser(queryClient, api) {
    return queryClient.prefetchQuery({
        queryKey: ['auth'],
        queryFn: ({ signal }) => api.auth.check(getAuthQuery(), { signal }),
    });
}

type UseAuthCheckOptions = Omit<
    Parameters<typeof useQuery<AuthResponse>>[0],
    'queryKey' | 'queryFn'
> & {
    snippetOnly?: boolean;
    keepPreviousData?: boolean;
};

export function useAuthCheck(opts: UseAuthCheckOptions = null) {
    const api = useApi();
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['auth'],
        queryFn: ({ signal }) => api.auth.check(getAuthQuery(), { signal }),
        ...opts,
    });

    return {
        user: null,
        ...data,
        ...queryResult,
    };
}

export function useAuthSendToken() {
    const api = useApi();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        { success: boolean },
        Error,
        LoginRequest
    >({
        mutationFn: (data) => api.auth.sendToken(data),
    });

    return {
        sendToken: mutate,
        sendTokenAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useAuthLogin() {
    const api = useApi();
    const queryClient = useQueryClient();
    const url = useCurrentUrl();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        AuthResponse,
        Error,
        LoginRequest
    >({
        mutationFn: (data) =>
            api.auth.login({
                url,
                ...data,
            }),
        onSuccess: (response) => {
            queryClient.setQueryData(['auth'], response);
        },
    });

    return {
        login: mutate,
        loginAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useAuthProviderLogin(provider) {
    const api = useApi();
    const queryClient = useQueryClient();
    const url = useCurrentUrl();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        AuthResponse,
        Error,
        ProviderLoginRequest
    >({
        mutationFn: (data) =>
            api.auth.providerLogin(provider, {
                url,
                ...data,
            }),
        onSuccess: (response) => {
            queryClient.setQueryData(['auth'], response);
        },
    });

    return {
        login: mutate,
        loginAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useAuthIdentify() {
    const api = useApi();
    const queryClient = useQueryClient();
    const updateConsent = useUpdateConsent();
    const url = useCurrentUrl();
    const { mutate, mutateAsync, ...mutationResult } = useMutation<
        AuthResponse,
        Error,
        LoginRequest
    >({
        mutationFn: (data) => api.auth.identify({ url, ...data }),
        onSuccess: (response) => {
            queryClient.setQueryData(['auth'], response);
            const { user = null } = response || {};
            if (user !== null) {
                updateConsent(true);
            }
        },
    });

    return {
        identify: mutate,
        identifyAsync: mutateAsync,
        ...mutationResult,
    };
}

export function useAuthLogout() {
    const api = useApi();
    const queryClient = useQueryClient();
    const sdk = useFacebookSdk();
    const { mutate, mutateAsync, ...mutationResult } = useMutation({
        mutationFn: () => api.auth.logout(),
        onSuccess: (response) => {
            queryClient.setQueryData(['auth'], response);
            if (sdk !== null) {
                sdk.logout();
            }
        },
    });

    return {
        logout: mutate,
        logoutAsync: mutateAsync,
        ...mutationResult,
    };
}
